import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { withTranslation } from 'react-i18next';
import { useQueryState } from 'react-router-use-location-state';
import {
    InternalGrid,
    ModalControl,
    ModalProvider,
    Box,
} from '@paygreen/paygreen-ui';
import { H1Title, ErrorBoundary, SimpleActionButton } from '../../components';
import { useAPIError } from '../../context';
import {
    formatDateApi,
    IsBigDesktop,
    IsDesktop,
    IsMobile,
} from '../../utils/tools';
import { endOfMonth, startOfMonth } from '../../utils/constants';
import DonationsTable from './DonationsTable';
import ExportDonationsModal from './ExportDonationsModal';
import FilterTable from './FilterTable';
import { getAllDonations } from './service';

const CharityDonation = ({ t }) => {
    const { addError } = useAPIError();
    const fetchIdRef = useRef(0);

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const [totalItems, setTotalItems] = useState(0);

    const [startDate, setStartDate] = useQueryState('startDate', startOfMonth);
    const [endDate, setEndDate] = useQueryState('endDate', endOfMonth);

    const fetchData = useCallback(
        ({ pageSize, pageIndex, statusFilter, startDate, endDate }) => {
            const fetchId = ++fetchIdRef.current;
            if (fetchId === fetchIdRef.current) {
                isMounted.current && setIsLoading(true);

                const params = {
                    limit: pageSize,
                    page: pageIndex + 1,
                    status: statusFilter,
                    begin: startDate && formatDateApi(startDate),
                    end: endDate && formatDateApi(endDate),
                };

                getAllDonations(params)
                    .then((res) => {
                        const {
                            page_count,
                            total_items,
                            _embedded: { donation },
                        } = res.data;

                        if (isMounted.current) {
                            setData(donation);
                            setPageCount(page_count);
                            setTotalItems(total_items);
                        }
                    })
                    .catch((error) => {
                        const { detail, status } = error?.response?.data;

                        if (status === 404) {
                            isMounted.current && setData([]);
                        } else {
                            addError(detail, status);
                        }
                    })
                    .finally(() => {
                        isMounted.current && setIsLoading(false);
                    });
            }
        },
        [addError],
    );

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    return (
        <>
            <Helmet>
                <title>
                    {t('CharityDonation.metaTitle')} - {t('main.metaTitle')}
                </title>
            </Helmet>

            <H1Title title={t('CharityDonation.title')}>
                {IsDesktop() && (
                    <ModalProvider>
                        <Box marginBottom="xs">
                            <ModalControl hasOnClickControl={true}>
                                <SimpleActionButton icon="DownloadIcon">
                                    {t('form.button.export')}
                                </SimpleActionButton>
                            </ModalControl>
                        </Box>

                        <ModalControl>
                            <ExportDonationsModal
                                startDate={startDate}
                                endDate={endDate}
                            />
                        </ModalControl>
                    </ModalProvider>
                )}
            </H1Title>

            <InternalGrid
                displayType="grid"
                gridGap="md"
                gridTemplateColumns="1fr"
                blockPadding="none"
                justifyItems="stretch"
                hasStaticWidth={IsBigDesktop() ? false : true}
            >
                <Box
                    marginBottom={IsDesktop() ? 'lg' : 'md'}
                    style={{
                        width: IsMobile() ? 'fit-content' : 'max-content',
                    }}
                >
                    <FilterTable
                        setEndDate={setEndDate}
                        setStartDate={setStartDate}
                        startDate={startDate}
                        endDate={endDate}
                    />
                </Box>

                <ErrorBoundary>
                    <DonationsTable
                        fetchData={fetchData}
                        data={data}
                        loading={isLoading}
                        pageCount={pageCount}
                        startDate={startDate}
                        endDate={endDate}
                        totalItems={totalItems}
                    />
                </ErrorBoundary>
            </InternalGrid>
        </>
    );
};

export default withTranslation('common')(CharityDonation);
